import React, { useState, useEffect } from 'react';
import { useApi } from '../../../../context/ApiContext/ApiContext';
import { ThemeProvider } from '../../themes/ThemeProvider';
import { processEndpointValues, restructureObject } from '../moduleUtils/apiObjectFormatting';
import FormField from '../baseElements/FormField';
import ModuleContainer from '../baseElements/ModuleContainer';
import ModuleHeader from '../baseElements/ModuleHeader';
import ModuleDescription from '../baseElements/ModuleDescription';
import ModuleBody from '../baseElements/ModuleBody';
import InfoComponent from '../../elements/InfoComponent';
import ButtonComponent from '../../elements/ButtonComponent';
import { moduleConfig } from './SmartplayIngestVODModuleConfig';
import { v4 as uuidv4 } from 'uuid';

const SmartplayIngestVOD = ({ id, data }) => {
  // API CALL CONTEXT
  const { moduleNodes, registerModule, updateModuleConfig, updateApiParams, setExecutionOrder, toggleModuleActive, state, isExecuting, isCloudProcessRunning } = useApi();
  const [endpointValues, setEndpointValues] = useState(() => {
    return moduleNodes[id] || 
        {
      "endpoint_0": {
        "path": "/ingest/external/jobs",
        "method": "POST",
        "root_url": "https://services.uplynk.com/api/v4",
        "params": {
          "job_type": "vod",
          "slicer_id": "SmartPlay_Ingest_VOD",    // get value from "Channel Title" formfield - but remove spaces, special character - can use underscore
          "url": "https://flow-demos.vercel.app/hls/Tears-of-Steel_hls/master.m3u8",
          "desc": "SmartPlay Ingest VOD",
          "persist_segments": true,
          "metadata": {
            "external_id": uuidv4(),
            "drm_mode": "none",
            "skip_drm": 1,
            "meta": {}
          },
          "state": "SCHEDULED"
        }
      },
    };
  });
  const [results, setResults] = useState(null);
  const [isModified, setIsModified] = useState(false);
  const isProcessing = isExecuting || isCloudProcessRunning;


  const handleChange = (endpointKey, paramName, value) => {
    // Create updates object starting with the current parameter
    const updates = {
      [endpointKey]: {
        params: {
          [paramName]: value
        }
      }
    };
    
    // For description field, generate sanitized ID and propagate to specified endpoints
    // if (endpointKey === "endpoint_0" && paramName === "desc" && typeof value === "string") {
    //   // Sanitize value for ID fields (lowercase, replace special chars with underscores)
    //   const sanitizedId = value.toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/_+/g, '_');
      
    //   // Map of target endpoints and their parameters that should receive the sanitized value
    //   const targetMap = {
    //     "endpoint_0": ["slicer_id"]
    //   };
      
    //   // Populate updates for all target endpoints
    //   Object.entries(targetMap).forEach(([targetEndpoint, params]) => {
    //     if (!updates[targetEndpoint]) {
    //       updates[targetEndpoint] = { params: {} };
    //     }
        
    //     params.forEach(param => {
    //       updates[targetEndpoint].params[param] = sanitizedId;
    //     });
    //   });
    // }
    
    // Apply all updates at once
    setEndpointValues(prev => {
      const newState = { ...prev };
      
      // Apply each endpoint update
      Object.entries(updates).forEach(([endpoint, update]) => {
        newState[endpoint] = {
          ...prev[endpoint],
          params: {
            ...prev[endpoint]?.params,
            ...update.params
          }
        };
      });
      
      return newState;
    });
    
    setIsModified(true);
  };

  // Handle form submission with API context integration
  // const handleSubmit = async () => {
  //   try {
  //     // Process the endpoint values
  //     const processedData = processEndpointValues(endpointValues, moduleConfig);
      
  //     if (!moduleNodes[id]) {
  //       // Module not registered - register it
  //       const moduleLabel = data?.label || '';
  //       const moduleData = {
  //         ...endpointValues,
  //         callbacks: moduleConfig.moduleConfig.callbacks,
  //       };
  //       registerModule(id, moduleData, moduleLabel, processedData.validation);
  //     } else {
  //       // Module exists - update it
  //       updateModuleConfig(id, processedData.endpoints, processedData.validation);
  //     }
      
  //     setIsModified(false);
  //   } catch (error) {
  //     console.error('Error processing form:', error);
  //   }
  // };
  const handleSubmit = async (endpointKey = "endpoint_0") => {
    try {
      // Generate UUID for the specified endpoint
      const updatedValues = { ...endpointValues };
      if (updatedValues[endpointKey]?.params?.metadata) {
        updatedValues[endpointKey].params.metadata.external_id = uuidv4();
      }
      
      // Process the updated endpoint values
      const processedData = processEndpointValues(updatedValues, moduleConfig);
      
      if (!moduleNodes[id]) {
        // Module not registered - register it
        const moduleLabel = data?.label || '';
        const moduleData = {
          ...updatedValues, // Use updated values with new UUID
          callbacks: moduleConfig.moduleConfig.callbacks,
        };
        registerModule(id, moduleData, moduleLabel, processedData.validation);
      } else {
        // Module exists - update it
        updateModuleConfig(id, processedData.endpoints, processedData.validation);
      }
      
      // Update state with the new values
      setEndpointValues(updatedValues);
      setIsModified(false);
    } catch (error) {
      console.error('Error processing form:', error);
    }
  };

  // Register module on mount
  useEffect(() => {
    if (!id) return;

    if (!moduleNodes[id]) {
      const moduleLabel = data?.label || '';

      // Process the endpoint values to get validation results
      const processedData = processEndpointValues(endpointValues, moduleConfig);
      
      // Create the full module data including callbacks
      const moduleData = {
        ...endpointValues,
        callbacks: moduleConfig.moduleConfig.callbacks, // Include callbacks from the module config
      };

      // Register module with all data including callbacks
      registerModule(id, moduleData, moduleLabel, processedData.validation);

    } else {
      setEndpointValues(moduleNodes[id]);
    }

    // Clean up when component unmounts
    return () => {
      // If you want to remove this module when the component unmounts
      // removeModule(nodeId);
    };
  }, []);


  useEffect(() => {
    console.log("smartplayIngestVODModule: ", state);
  }, [state])

  return (
      <ThemeProvider themeKey="uplynk" size="small">
        <ModuleContainer>
          <ModuleHeader title="SmartPlay:Ingest VOD" />
          <ModuleDescription description="" />
          <ModuleBody endpoints={moduleConfig.moduleConfig.endpoints} readyState={moduleNodes[id]?.readyState && !isModified} activeState={moduleNodes[id]?.activeState} toggleModuleActive={toggleModuleActive} id={id}>

              {/* Field: desc for /ingest/external/jobs* */}
                <FormField 
                  type="text"
                  label="Job Title"
                  value={endpointValues['endpoint_0']?.params?.['desc']}
                  onChange={(value) => handleChange('endpoint_0', 'desc', value)}
                  placeholder="Sports Highlight"
                />

              {/* Field: url for /ingest/external/jobs* */}
              <div className="mt-1">
                <FormField 
                  type="textarea"
                  label="Content Source URL"
                  value={endpointValues['endpoint_0']?.params?.['url']}
                  onChange={(value) => handleChange('endpoint_0', 'url', value)}
                  required={true}
                  placeholder="https://c4af3793bf76b33c.mediapackage.us-west-2.amazonaws.com/out/v1/a2a60bf0d27049058a175a3f968ded34/index.m3u8"
                />
              </div>


              {/* Field: persist_segments for /ingest/external/jobs* */}
                <FormField 
                  type="checkbox"
                  label="Store Segments"
                  value={endpointValues['endpoint_0']?.params?.['persist_segments']}
                  onChange={(value) => handleChange('endpoint_0', 'persist_segments', value)}
                  placeholder="false"
                />

              {/* Field: require_drm for /api2/channel/create - Integer Checkbox */}
                {/* <FormField 
                  type="checkbox"
                  label="Token"
                  value={!!endpointValues['endpoint_1']?.params?.['require_drm']}
                  onChange={(checked) => handleChange('endpoint_1', 'require_drm', checked ? 1 :0)}
                /> */}

              {/* Field: require_studio_drm for /api2/channel/create - Integer Checkbox */}
                {/* <FormField 
                  type="checkbox"
                  label="Studio DRM"
                  value={!!endpointValues['endpoint_1']?.params?.['require_studio_drm']}
                  onChange={(checked) => handleChange('endpoint_1', 'require_studio_drm', checked ? 1 :0)}
                /> */}

              <ButtonComponent 
                id="button_1741528109211"
                label={isProcessing ? "Processing..." : "Apply"}
                variant={isProcessing ? "secondary" : "primary"}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSubmit();
                }}
                readyState={!isProcessing && moduleNodes[id]?.readyState && !isModified}
                executing={isProcessing}
                disabled={isProcessing}
                isProcessing={isProcessing}
              />
          </ModuleBody>
        </ModuleContainer>
      </ThemeProvider>
    );
}

export default SmartplayIngestVOD;